import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import ContactForm, { type ContactFormValues } from '../components/ContactForm';

export default function ContactEditorPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { items, addItem, updateItem } = useSafe();
  const existing = id ? items.find((i) => i.id === id && i.type === 'contact' && !i.orgId) : undefined;
  const isEdit = !!existing;
  const [loading, setLoading] = useState(false);

  async function handleSubmit(values: ContactFormValues): Promise<void> {
    setLoading(true);
    try {
      const payload = {
        type: 'contact' as const, ...values,
        sensitive: existing?.sensitive ?? false, favorite: existing?.favorite ?? false, tags: existing?.tags ?? [],
      };
      if (isEdit && existing) { await updateItem(existing.id, payload); navigate(`/contacts/${existing.id}`); }
      else { const created = await addItem(payload); navigate(`/contacts/${created.id}`); }
    } finally { setLoading(false); }
  }

  return (
    <ContactForm
      // Remount on route change so useState field seeds re-run. Both
      // /contacts/new and /contacts/:id/edit render this page into the same
      // persistent ContactsLayout outlet; without a key, React reuses the
      // ContactForm instance and a prior contact's data lingers when you
      // switch to "+ New" (or between two contacts).
      key={id ?? 'new'}
      heading={isEdit && existing ? `Edit ${existing.name}` : 'New Contact'}
      initial={existing} isEdit={isEdit} loading={loading}
      onSubmit={handleSubmit}
      onCancel={() => navigate(isEdit && existing ? `/contacts/${existing.id}` : '/contacts')}
    />
  );
}
